import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { popularMangaList, categories } from '@/mocks/manga';

function CategorySchemaOrg() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: '漫画分类 - 古古漫画网',
        url: 'http://sylbdj.com/category',
        description: '古古漫画网漫画分类页面，按热血、搞笑、恋爱、奇幻、科幻等类型浏览海量漫画资源，快速找到您喜爱的漫画作品。',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://sylbdj.com/' },
            { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://sylbdj.com/category' },
          ],
        },
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('type') || 'all';

  const filteredManga = useMemo(() => {
    if (activeCategory === 'all') return popularMangaList;
    const cat = categories.find((c) => c.id === activeCategory);
    if (!cat) return popularMangaList;
    return popularMangaList.filter((m) => m.category === cat.name);
  }, [activeCategory]);

  const activeCatName = categories.find((c) => c.id === activeCategory)?.name || '全部';

  return (
    <>
      <CategorySchemaOrg />
      <Navbar />

      <main>
        {/* Category Header */}
        <section className="bg-background-900 pt-24 pb-16 md:pt-28 md:pb-20" aria-label="漫画分类头部">
          <div className="w-full px-4 md:px-6 text-center">
            <span className="whitespace-nowrap text-xs text-background-400/70 tracking-wider">
              漫画分类
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mt-3 leading-tight">
              探索漫画世界
            </h1>
            <p className="font-heading text-2xl md:text-3xl text-background-50/60 mt-2">
              找到你的下一部最爱
            </p>

            <div className="flex justify-center gap-3 mt-10">
              <Link
                to="/manga/shuumatsu-no-valkyrie"
                className="whitespace-nowrap px-6 py-2.5 bg-background-50 text-foreground-900 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer"
              >
                热门推荐
              </Link>
              <button
                className="whitespace-nowrap px-6 py-2.5 border border-background-300/40 text-sm text-background-200 rounded-md hover:border-background-200 hover:text-background-50 transition-colors cursor-pointer"
                onClick={() => {
                  setSearchParams({});
                }}
              >
                全部漫画
              </button>
            </div>

            <div className="flex flex-wrap gap-3 justify-center mt-14">
              {categories.slice(0, 10).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    if (cat.id === 'all') {
                      setSearchParams({});
                    } else {
                      setSearchParams({ type: cat.id });
                    }
                  }}
                  className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-background-50/15 text-background-50 border border-background-50/20'
                      : 'bg-background-800 text-background-300/70 hover:bg-background-700/50 border border-transparent'
                  }`}
                >
                  <i className={`${cat.icon} text-base`} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span>/</span>
              <span className="text-foreground-800">{activeCatName}</span>
            </nav>
          </div>
        </div>

        {/* Manga Grid */}
        <section className="bg-background-50 py-10 md:py-14" aria-label="漫画列表">
          <div className="w-full px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              {activeCategory === 'all' ? '全部漫画' : activeCatName + '漫画'}
              <span className="text-sm font-normal text-foreground-500 ml-2">
                (共{filteredManga.length}部)
              </span>
            </h2>

            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
                {filteredManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    className="group cursor-pointer rounded-lg overflow-hidden relative aspect-[3/4]"
                  >
                    <img
                      src={manga.coverImage}
                      alt={`${manga.title}漫画`}
                      title={`${manga.title}漫画在线免费阅读 - 古古漫画网`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-5">
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {manga.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="whitespace-nowrap px-2 py-0.5 bg-white/15 text-white/80 text-[10px] rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-heading text-lg font-bold text-white">
                        {manga.title}
                      </h3>
                      <p className="text-xs text-white/70 mt-1">{manga.author}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-accent-300">
                          <i className="ri-star-fill mr-0.5" />{manga.rating}
                        </span>
                        <span className="text-xs text-white/50">{manga.status}</span>
                        <span className="text-xs text-white/50">{manga.views}阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
                <p className="text-foreground-500 mt-4">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}